import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

import ApplianceCategoryPicker from "./ApplianceCategoryPicker";
import ResultScreen from "./ResultScreen";

import { getLoadCalculation } from "./loadCalculator.service";
import { calculateTotalLoad, clampQuantity, validateSelection, isStepValid } from "./loadCalculator.utils";
import {
  APPLIANCE_CATEGORIES,
  BACKUP_HOURS_LIMITS,
  LOAD_TYPES,
  STEPS,
} from "./loadCalculator.constants";
import { RevealGroup } from "@/components/Reveal";

/**
 * LoadCalculator
 *
 * Appliance-picker flow matching the reference tool (luminousindia.com/load-calculator):
 *   1. Browse appliance categories, filter by Essential/Heavy load, and set
 *      quantities per appliance — the total running load updates live.
 *   2. Choose average daily backup hours and hit "Find Solution" to get a
 *      recommended inverter + battery.
 *
 * All calculation logic lives in utils/services; this component only owns
 * UI state (quantities, active category/filter,
 * backup hours, errors, loading, result).
 */
export default function LoadCalculator() {
  const [step, setStep] = useState(STEPS.APPLIANCES);
  const [quantities, setQuantities] = useState({});
  const [activeCategoryId, setActiveCategoryId] = useState(APPLIANCE_CATEGORIES[0].id);
  const [loadTypeFilter, setLoadTypeFilter] = useState(LOAD_TYPES.ALL);
  const [backupHours, setBackupHours] = useState(BACKUP_HOURS_LIMITS.default);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const totalLoad = useMemo(() => calculateTotalLoad(quantities), [quantities]);

  const handleQuantityDelta = (applianceId, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [applianceId]: clampQuantity((prev[applianceId] || 0) + delta),
    }));
    if (errors.totalLoad) setErrors({});
  };

  const handleCalculate = async () => {
    const selectionErrors = validateSelection(quantities);
    if (!isStepValid(selectionErrors)) {
      setErrors(selectionErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const calcResult = await getLoadCalculation({ quantities, backupHours });
      setResult(calcResult);
      setStep(STEPS.RESULT);
    } catch (e) {
      console.error("Load calculation failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToAppliances = () => setStep(STEPS.APPLIANCES);

  const handleRestart = () => {
    setQuantities({});
    setActiveCategoryId(APPLIANCE_CATEGORIES[0].id);
    setLoadTypeFilter(LOAD_TYPES.ALL);
    setBackupHours(BACKUP_HOURS_LIMITS.default);
    setErrors({});
    setResult(null);
    setStep(STEPS.APPLIANCES);
  };

  return (
    <section
      id="load-calculator"
      className="relative py-24 md:py-32 bg-white overflow-hidden"
      data-testid="load-calculator-section"
    >
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealGroup className="text-center mb-8">
          {/* <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F26A21] mb-3">
            Calculator
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Find the right <span className="text-[#1B3A8C]">inverter &amp; battery</span> for your home.
          </h2>
          <p className="mt-3 text-slate-500 max-w-xl mx-auto">
            Pick your appliances, and we'll size the exact backup power you need.
          </p> */}
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F26A21] mb-3">Load Calculator</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]">
            Find the right <span className="text-[#2BA84A]">inverter &amp; battery</span> for your home.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Pick your appliances, and we'll size the exact backup power you need.
          </p>
        </RevealGroup>

        <div className="rounded-3xl border border-slate-100 shadow-xl shadow-blue-900/5 overflow-hidden lg:grid lg:grid-cols-[1fr_340px]">
          <AnimatePresence mode="sync" initial={false}>
            {step === STEPS.APPLIANCES && (
              <motion.div
                key="appliances"
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="lg:contents"
              >
                <div className="bg-white p-5 md:p-8" data-testid="load-calc-appliances-step">
                  <div>
                    <ApplianceCategoryPicker
                      categories={APPLIANCE_CATEGORIES}
                      activeCategoryId={activeCategoryId}
                      onCategoryChange={setActiveCategoryId}
                      loadTypeFilter={loadTypeFilter}
                      onLoadTypeFilterChange={setLoadTypeFilter}
                      quantities={quantities}
                      onQuantityDelta={handleQuantityDelta}
                    />
                  </div>
                </div>

                <div className="bg-slate-50 p-5 md:p-8 flex flex-col border-t border-slate-100 lg:border-t-0 lg:border-l">
                  <p className="text-sm font-bold text-slate-800">Your Total Running Load</p>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span
                      data-testid="total-running-load-value"
                      className="font-display text-5xl font-extrabold text-[#1B3A8C]"
                    >
                      {totalLoad}
                    </span>
                    <span className="text-lg font-bold text-[#1B3A8C]">Watts*</span>
                  </div>
                  <span data-testid="total-running-load" className="sr-only">
                    {totalLoad} W
                  </span>
                  {errors.totalLoad && (
                    <p className="mt-2 text-xs font-medium text-red-600">{errors.totalLoad}</p>
                  )}

                  <p className="mt-3 text-xs text-slate-400">
                    Get product suggestions as per your required backup power. Choose your estimate below.
                  </p>

                  <div className="mt-6">
                    <label className="text-sm font-bold text-slate-800 flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#F26A21]" /> Average Daily Backup (Hrs)
                      </span>
                      <span className="text-[#1B3A8C] font-display text-lg">{backupHours} hr</span>
                    </label>
                    <Slider
                      value={[backupHours]}
                      onValueChange={(v) => setBackupHours(v[0])}
                      min={BACKUP_HOURS_LIMITS.min}
                      max={BACKUP_HOURS_LIMITS.max}
                      step={BACKUP_HOURS_LIMITS.step}
                      className="mt-4"
                      data-testid="backup-hours-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1.5">
                      <span>{BACKUP_HOURS_LIMITS.min} hr</span>
                      <span>{BACKUP_HOURS_LIMITS.max} hr</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCalculate}
                    disabled={loading}
                    data-testid="load-calc-submit-btn"
                    className="w-full mt-6 lg:mt-auto rounded-xl bg-[#F26A21] hover:bg-[#D95B1A] text-white py-6 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {loading ? "Calculating…" : "Find Solution"}
                  </Button>
                </div>
              </motion.div>
            )}

            {step === STEPS.RESULT && (
              <motion.div
                key="result"
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-white p-5 md:p-8 lg:col-span-2"
              >
                <ResultScreen result={result} onBack={handleBackToAppliances} onRestart={handleRestart} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-4 text-center text-xs text-slate-400">
          *Disclaimer: Calculations are indicative and based on standard assumptions. Actual outputs may vary.
        </p>
      </div>
    </section>
  );
}
